// libs
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

// interfaces / constants
import { COLOR_TYPE_PRIMARY } from 'src/utils/color'

// classes / components
import LegacyButton, { BUTTON_TYPE_CONTAINED, BUTTON_TYPE_TEXT } from 'src/components/forms/button/button'
import FieldSet from 'src/components/forms/field_set/field_set'
import InputField from 'src/components/forms/input_field/input_field'
import MessageBoxWithModal from 'src/components/message_box/message_box_with_modal'
import MessageSegmentWithModal from 'src/components/message_segment/message_segment_with_modal'
import NavigationItem from 'src/components/navigation_item/navigation_item'

// helpers
import { textResources } from 'src/lang/de'
import { createBemBlock } from 'src/utils/bem_helper/bem_helper'
import initTracker, { BLOG_PROFILE_ADMIN } from 'src/utils/reporting/events_tracking/events_tracking'
import { validateEmail } from 'src/utils/validation'

import { useCreateFeedback, useUser } from '@lopo-tech/lopo-api'
import './blog_profile_admin_modal.scss'

const tracker = initTracker(BLOG_PROFILE_ADMIN)
const labels = textResources.manageBlogProfileAdmin

interface IProps {
  onClose: () => void;
  profileName: string;
}

const cls = createBemBlock('blog_profile_admin_modal')

const BlogProfileAdminModal: React.FC<IProps> = ({ onClose, profileName }) => {
  const [emails, setEmails] = useState<string>('')
  const [isValid, setIsValid] = useState<boolean>(true)
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { data: userData } = useUser()
  const [createFeedback] = useCreateFeedback()

  // componentDidMount equivalent
  useEffect(() => {
    tracker(BLOG_PROFILE_ADMIN.ACTIONS.REQUEST, BLOG_PROFILE_ADMIN.LABELS.START)
  }, [])

  const changeEmails = useCallback((value: string) => {
    setEmails(value)
    setIsValid(true)
  }, [])

  const submitForm = useCallback(() => {
    const validEmail = validateEmail(emails, true)
    if (!validEmail) {
      setIsValid(false)
      return
    }

    setIsLoading(true)

    createFeedback({
      variables: {
        input: {
          email: userData?.me.email,
          message: `Anfrage: Seiten-Administrator hinzufügen E-Mail-Addressen: ${emails}`
        }
      }
    }).then(() => {
      setFormSubmitted(true)
      setIsLoading(false)
      tracker(BLOG_PROFILE_ADMIN.ACTIONS.REQUEST, BLOG_PROFILE_ADMIN.LABELS.FINISH)
    })
  }, [emails])

  if (formSubmitted) {
    return (
      <MessageSegmentWithModal
        image={'admin-success-screen'}
        title={labels.formSuccessTitle}
        description={(
          <>
            {labels.formSuccessBodyStart}<strong>{emails}</strong>{labels.formSuccessBodyEnd}
            <NavigationItem
              className={cls('info-link')}
              target={'_blank'}
              url={`mailto:${textResources.shared.helpEmail}`}
            >
              {textResources.shared.helpEmail}
            </NavigationItem>
          </>
        )}
        onClose={onClose}
      />
    )
  }

  return (
    <MessageBoxWithModal
      isLoading={isLoading}
      onClose={onClose}
      whiteBackground
      title={labels.menuName}
    >
      <div className={cls('body')}>
        {labels.formBodyStart}<strong>{profileName}</strong>{labels.formBodyEnd}
      </div>
      <div className={cls('input')}>
        <FieldSet>
          <InputField
            assistiveText={textResources.shared.formAssistiveText}
            name='email'
            label={textResources.spread.formFieldEmailsPlaceholder}
            onChange={changeEmails}
            value={emails}
            valid={isValid}
            errorText={textResources.shared.formErrorInvalid}
            required
            type='email'
          />
        </FieldSet>
      </div>
      <LegacyButton
        variant={BUTTON_TYPE_CONTAINED}
        color={COLOR_TYPE_PRIMARY}
        label={labels.menuName}
        fullWidth
        onClick={submitForm}
        lowerCase
      />

      <div className={cls('footer')}>
        <LegacyButton
          variant={BUTTON_TYPE_TEXT}
          fullWidth
          onClick={onClose}
          label={textResources.shared.cancel}
          lowerCase
        />
      </div>
    </MessageBoxWithModal>
  )
}

export default React.memo(BlogProfileAdminModal)
